<div class="container">
  <!-- Stepper Header -->
  <div class="stepper-container">
    <div class="stepper">
      <!-- Step 1: Upload -->
      <div class="step" [class.active]="currentStep >= 1" [class.completed]="currentStep > 1">
        <div class="step-circle">
          <mat-icon *ngIf="currentStep > 1">check</mat-icon>
          <span *ngIf="currentStep <= 1">1</span>
        </div>
        <div class="step-label">Upload Files</div>
      </div>

      <div class="step-line" [class.active]="currentStep >= 2"></div>

      <!-- Step 2: Processing -->
      <div class="step" [class.active]="currentStep >= 2" [class.completed]="currentStep > 2">
        <div class="step-circle">
          <mat-icon *ngIf="currentStep > 2">check</mat-icon>
          <mat-spinner *ngIf="currentStep === 2" diameter="24"></mat-spinner>
          <span *ngIf="currentStep < 2">2</span>
        </div>
        <div class="step-label">Processing</div>
      </div>

      <div class="step-line" [class.active]="currentStep >= 3"></div>

      <!-- Step 3: Complete -->
      <div class="step" [class.active]="currentStep >= 3" [class.completed]="currentStep >= 3">
        <div class="step-circle">
          <mat-icon *ngIf="currentStep >= 3">
            {{ jobStatus?.status === 'FAILED' ? 'error' : 'check' }}
          </mat-icon>
          <span *ngIf="currentStep < 3">3</span>
        </div>
        <div class="step-label">Complete</div>
      </div>
    </div>
  </div>

  <!-- Main Card -->
  <div class="main-card">
    <!-- Step 1: Upload Form -->
    <div class="step-content" *ngIf="currentStep === 1">
      <h2 class="section-title">Upload Video Library</h2>
      <p class="section-description">Upload Excel metadata and video ZIP file</p>

      <form #videoLibraryForm="ngForm">
        <!-- Client Selection -->
        <mat-form-field appearance="outline" color="primary" class="full-width">
          <mat-label>Client</mat-label>
          <input type="text" matInput [formControl]="clientControl" [matAutocomplete]="auto" required>
          <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayClient.bind(this)"
            (optionSelected)="onClientSelected($event.option.value)">
            <mat-option *ngFor="let client of filteredClients | async" [value]="client">
              {{client.dname}}
            </mat-option>
          </mat-autocomplete>
          <mat-error>Client is required</mat-error>
        </mat-form-field>

        <!-- Excel File Upload -->
        <div class="file-upload-section">
          <button color="primary" class="file-button" type="button" mat-stroked-button (click)="excelFileInput.click()">
            <mat-icon>table_chart</mat-icon>
            Excel Metadata File
          </button>
          <input hidden (change)="onExcelFileSelected($event)" #excelFileInput name="excelFile" type="file"
            accept=".xls, .xlsx" required />

          <div class="file-info" *ngIf="videoLibrary.excelFile?.name">
            <mat-icon class="file-icon">description</mat-icon>
            <span class="file-name">{{ videoLibrary.excelFile?.name }}</span>
            <button mat-icon-button (click)="removeExcelFile()" class="remove-btn">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>

        <!-- ZIP File Upload -->
        <div class="file-upload-section">
          <button color="primary" class="file-button" type="button" mat-stroked-button (click)="zipFileInput.click()">
            <mat-icon>video_library</mat-icon>
            Video ZIP Archive
          </button>
          <input hidden (change)="onZipFileSelected($event)" #zipFileInput name="zipFile" type="file" accept=".zip"
            required />

          <div class="file-info" *ngIf="videoLibrary.zipFile?.name">
            <mat-icon class="file-icon">folder_zip</mat-icon>
            <span class="file-name">{{ videoLibrary.zipFile?.name }}</span>
            <button mat-icon-button (click)="removeZipFile()" class="remove-btn">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <!-- Validate Upload Button -->
          <button color="primary" class="upload-btn" [disabled]="!videoLibraryForm.valid || uploading"
            (click)="upload()" mat-raised-button>
            <mat-icon *ngIf="!uploading">check_circle</mat-icon>
            <mat-spinner *ngIf="uploading && !videoLibrary.commit" diameter="20" class="btn-spinner"></mat-spinner>
            {{ uploading && !videoLibrary.commit ? 'Validating...' : 'Validate Upload' }}
          </button>

          <!-- Proceed to Upload Button (directly with commit=true) -->
          <button color="accent" class="upload-btn" [disabled]="!videoLibraryForm.valid || uploading"
            (click)="proceedToUploadDirect()" mat-raised-button>
            <mat-icon *ngIf="!uploading">cloud_upload</mat-icon>
            <mat-spinner *ngIf="uploading && videoLibrary.commit" diameter="20" class="btn-spinner"></mat-spinner>
            {{ uploading && videoLibrary.commit ? 'Uploading...' : 'Proceed to Upload' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Step 2: Processing -->
    <div class="step-content" *ngIf="currentStep === 2">
      <h2 class="section-title">Processing Upload</h2>
      <p class="section-description">{{ jobStatus?.step || 'Initializing...' }}</p>

      <div class="progress-section">
        <!-- Circular Progress -->
        <div class="circular-progress">
          <mat-progress-spinner mode="determinate" [value]="jobStatus?.progress || 0" diameter="120" strokeWidth="8"
            color="primary">
          </mat-progress-spinner>
          <div class="progress-value">{{ jobStatus?.progress || 0 }}%</div>
        </div>

        <!-- Status Info -->
        <div class="status-info">
          <div class="status-badge" [class.processing]="jobStatus?.status === 'PROCESSING'">
            <mat-icon>{{ jobStatus?.status === 'PROCESSING' ? 'sync' : 'schedule' }}</mat-icon>
            {{ jobStatus?.status || 'PENDING' }}
          </div>
          <p class="status-message">{{ jobStatus?.step }}</p>
          <p class="polling-info">
            <mat-icon class="small-icon">update</mat-icon>
            Status updates every 5 seconds
          </p>
        </div>
      </div>
    </div>

    <!-- Step 3: Complete -->
    <div class="step-content" *ngIf="currentStep === 3">
      <!-- Success -->
      <div class="result-container" *ngIf="jobStatus?.status === 'SUCCESS'">
        <div class="success-header">
          <mat-icon class="result-icon success">check_circle</mat-icon>
          <h2 class="result-title">Upload Complete!</h2>
        </div>

        <div class="success-message" *ngIf="jobStatus.details?.message">
          <mat-icon>info</mat-icon>
          {{ jobStatus.details.message }}
        </div>

        <div class="action-row">
          <button *ngIf="!videoLibrary.commit" mat-raised-button color="primary" (click)="promoteToUpload()"
            class="action-btn">
            <mat-icon>cloud_upload</mat-icon>
            Proceed to Upload
          </button>

          <button mat-raised-button color="accent" (click)="resetForm()" class="action-btn">
            <mat-icon>refresh</mat-icon>
            Upload Another
          </button>
        </div>
      </div>

      <!-- Error -->
      <div class="result-container" *ngIf="jobStatus?.status === 'FAILED'">
        <div class="error-header">
          <mat-icon class="result-icon error">error</mat-icon>
          <h2 class="result-title">Upload Failed</h2>
        </div>

        <div class="error-message">
          <p><strong>Error:</strong> {{ jobStatus.details?.error || jobStatus.details?.message || 'Unknown error occurred' }}</p>
        </div>

        <!-- Failed Details - Multiple Errors -->
        <div class="failed-details" *ngIf="jobStatus.details?.errors && jobStatus.details.errors.length > 0">
          <h3>
            <mat-icon color="warn">warning</mat-icon>
            Validation Errors ({{ jobStatus.details.errors.length }})
          </h3>
          <p class="error-summary">Please fix the following issues and re-upload:</p>

          <div class="error-list">
            <div class="error-item" *ngFor="let error of jobStatus.details.errors; let i = index">
              <div class="error-header-item">
                <mat-icon color="warn">error_outline</mat-icon>
                <span class="error-number">Error {{ i + 1 }} of {{ jobStatus.details.errors.length }}</span>
                <span class="error-row" *ngIf="error.row > 0">Row {{ error.row }}</span>
              </div>

              <div class="error-body">
                <div class="detail-grid">
                  <div class="detail-item" *ngIf="error.feature">
                    <span class="detail-label">Feature:</span>
                    <span class="detail-value">{{ error.feature }}</span>
                  </div>
                  <div class="detail-item" *ngIf="error.category">
                    <span class="detail-label">Category:</span>
                    <span class="detail-value">{{ error.category }}</span>
                  </div>
                  <div class="detail-item" *ngIf="error.videoTitle">
                    <span class="detail-label">Video Title:</span>
                    <span class="detail-value">{{ error.videoTitle }}</span>
                  </div>
                  <div class="detail-item" *ngIf="error.videoFilename">
                    <span class="detail-label">Filename:</span>
                    <span class="detail-value">{{ error.videoFilename }}</span>
                  </div>
                </div>

                <div class="error-message-box">
                  <strong>Error:</strong> {{ error.error }}
                </div>

                <div class="resolution-box">
                  <mat-icon>lightbulb</mat-icon>
                  <div>
                    <strong>How to fix:</strong> {{ error.resolution }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button mat-raised-button color="accent" (click)="resetForm()" class="reset-btn">
          <mat-icon>refresh</mat-icon>
          Try Again
        </button>
      </div>
    </div>
  </div>
</div>